import React from 'react';
import SaleConfirmationSchema from './SaleConfirmationDialog.form';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { ISaleConfirmationDialogProperties } from './SaleConfirmationDialog.properties';
import { DialogTitle, ToogleInvoiceFields } from './SaleConfirmationDialog.constants';
import { useSPContextSelector } from '../Worksale.context';
import Selectors from '../Worksale.selectors';
import { useFetchSaleConfirmation } from 'api/Worksale/worksale';
import { isNull } from 'utils/utils';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';

const SaleConfirmationDialog = (props: ISaleConfirmationDialogProperties) => {
    const {
        open, loading, formValues,
        invoiceDocumentOutputOptions, setInvoiceDocumentOutputOptions, clearInvoiceDocumentOutputOptions,
        salesDocketDocumentOutputOptions, setSalesDocketDocumentOutputOptions, clearSalesDocketDocumentOutputOptions,
        pickSlipDocumentOutputOptions, setPickSlipDocumentOutputOptions, clearPickSlipDocumentOutputOptions,
        orderDocumentOutputOptions, setOrderDocumentOutputOptions, clearOrderDocumentOutputOptions,
        barcodeLabelOutput, setBarcodeLabelOutputOptions, clearBarcodeLabelOutputOptions,
        onOk, onCancel, changeFormFieldValue, operationInProgress, processSaleQuery, destroyForm, formSyncErrors
    } = props;
    const worksaleId = useSPContextSelector(Selectors.WorksaleId) || -1;
    const { data: response, isLoading } = useFetchSaleConfirmation(worksaleId);

    const customerId = useSPContextSelector(Selectors.CustomerId);
    const inlineResponse = response?.SaleConfirmation?.inlineObject;
    const initialValues = React.useMemo(
        () => {
            if (operationInProgress === 'ProcessSale') {
                return processSaleQuery;
            } else {
                if (formValues) {
                    return {
                        ...formValues,
                        InvoiceOutput: invoiceDocumentOutputOptions,
                        OrderAcknowledgementOutput: orderDocumentOutputOptions,
                        PickSlipOutput: pickSlipDocumentOutputOptions,
                        SalesDocketOutput: salesDocketDocumentOutputOptions,
                        BarcodeLabelOutput: barcodeLabelOutput
                    };
                } else if (!isNull(inlineResponse)) {
                    return {
                        ...inlineResponse,
                        InvoiceOutput: !isNull(invoiceDocumentOutputOptions) ? invoiceDocumentOutputOptions : response?.SaleConfirmation?.InvoiceOutput?.inlineObject,
                        OrderAcknowledgementOutput: !isNull(orderDocumentOutputOptions) ? orderDocumentOutputOptions : response?.SaleConfirmation?.OrderAcknowledgementOutput?.inlineObject,
                        PickSlipOutput: !isNull(pickSlipDocumentOutputOptions) ? pickSlipDocumentOutputOptions : response?.SaleConfirmation?.PickSlipOutput?.inlineObject,
                        SalesDocketOutput: !isNull(salesDocketDocumentOutputOptions) ? salesDocketDocumentOutputOptions : response?.SaleConfirmation?.SalesDocketOutput?.inlineObject,
                        BarcodeLabelOutput: !isNull(barcodeLabelOutput) ? barcodeLabelOutput : response?.SaleConfirmation?.BarcodeLabelOutput?.inlineObject
                    };

                }

                return {};
            }
        },
        [operationInProgress, processSaleQuery, response, formValues]
    );
    const valuesSchema = React.useMemo(() => (response?.SaleConfirmation?.schema), [response]);

    const resetDocOutOptions = () => {
        destroyForm();
        clearInvoiceDocumentOutputOptions();
        clearSalesDocketDocumentOutputOptions();
        clearPickSlipDocumentOutputOptions();
        clearOrderDocumentOutputOptions();
        clearBarcodeLabelOutputOptions();
    };

    React.useEffect(
        () => {
            if (open && initialValues) {
                setInvoiceDocumentOutputOptions(initialValues?.InvoiceOutput);
                setSalesDocketDocumentOutputOptions(initialValues?.SalesDocketOutput);
                setPickSlipDocumentOutputOptions(initialValues?.PickSlipOutput);
                setBarcodeLabelOutputOptions(initialValues?.BarcodeLabelOutput);
                setOrderDocumentOutputOptions(initialValues?.OrderAcknowledgementOutput);
            } else {
                resetDocOutOptions();
            }
        },
        [initialValues, open]);

    const toogleInvoiceSale = (toogle: boolean): void => {
        ToogleInvoiceFields.forEach((fieldName) => {
            changeFormFieldValue(fieldName, toogle ? valuesSchema[fieldName].Value : toogle);
        });
    };

    const validData = (): boolean => {
        if (formSyncErrors && Object.keys(formSyncErrors).length > 0) {
            const requiredError = Object.entries(formSyncErrors).find((item) => item.includes('Required'));
            if (requiredError) {
                showSnackBar({ variant: 'warning', message: 'Please fill in required fields.' });
            } else {
                const firstError = Object.keys(formSyncErrors)[0];
                showSnackBar({ variant: 'warning', message: `Please enter valid ${firstError}` });
            }

            return false;
        }

        return true;
    };

    return (
        <div>
            <FormViewModal
                open={open}
                loading={loading}
                title={DialogTitle}
                formProps={{
                    formSchema: SaleConfirmationSchema,
                    initialValues: {
                        ...initialValues,
                        InvoiceOutput: initialValues && initialValues.InvoiceOutput ? initialValues.InvoiceOutput.Summary : '',
                        SalesDocketOutput: initialValues && initialValues.SalesDocketOutput ? initialValues.SalesDocketOutput.Summary : '',
                        PickSlipOutput: initialValues && initialValues.PickSlipOutput ? initialValues.PickSlipOutput.Summary : '',
                        OrderAcknowledgementOutput: initialValues && initialValues.OrderAcknowledgementOutput ? initialValues.OrderAcknowledgementOutput.Summary : '',
                        BarcodeLabelOutput: initialValues && initialValues.BarcodeLabelOutput ? initialValues.BarcodeLabelOutput.Summary : ''
                    },
                    fieldExtensions: {
                        InvoiceSale: {
                            onChange: (e) => {
                                toogleInvoiceSale(e.target.checked);
                            }
                        },
                        OutputInvoice: {
                            disabled: !(formValues && formValues?.InvoiceSale),
                        },
                        InvoiceOutput: {
                            disabled: !(formValues && formValues?.InvoiceSale)
                        },
                        SalesDocketOutput: {
                            disabled: !(formValues && formValues?.InvoiceSale)
                        },
                        OutputBarcodeLabel: {
                            disabled: (valuesSchema?.OutputBarcodeLabel?.ReadOnly || !(formValues && formValues?.InvoiceSale))
                        },
                    },
                    valuesSchema: valuesSchema ? valuesSchema : {},
                    style: { height: '600px', minWidth: '600px' },
                    customerId: customerId
                }}
                actions={[
                    {
                        title: 'Ok',
                        disabled: isLoading,
                        listener: () => {
                            if (onOk) {
                                if (validData()) {
                                    onOk({
                                        ...formValues,
                                        InvoiceOutput: invoiceDocumentOutputOptions,
                                        SalesDocketOutput: salesDocketDocumentOutputOptions,
                                        PickSlipOutput: pickSlipDocumentOutputOptions,
                                        OrderAcknowledgementOutput: orderDocumentOutputOptions,
                                        BarcodeLabelOutput: barcodeLabelOutput,
                                    });
                                }
                            }
                        }
                    },
                    {
                        title: 'Cancel',
                        isDefault: true,
                        listener: () => {
                            if (onCancel) {
                                onCancel();
                            }
                        },
                    },
                ]}
                dialogActionsButtons={true}
                dialogActionsShadow={false}
            />
        </div>
    );
};

export default SaleConfirmationDialog;
